import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { api } from '../../api';
import { NOME_NIVEL, NOME_STATUS_ATIVO, type Nivel } from './comum';

// Parque instalado em árvore: Cliente → Parque (local) → Via/Setor → equipamento → componente.
// Aparece quando a lista está filtrada por cliente ou parque. Seleção + "Mover para" pendura vários de uma vez
// (condomínio cadastrado solto antes das vias existirem).

export interface ItemArvore {
  id: string; apelido: string; status: string; agrupador?: boolean; ativo_pai_id: string | null;
  fabricante: string | null; modelo: string | null; ip: string | null; categoria: string; local_id: string; local: string;
  quantidade?: number; nivel_efetivo?: Nivel;
}
interface No { a: ItemArvore; filhos: No[]; total: number }

const essencial = (a: ItemArvore) => a.agrupador || !a.nivel_efetivo || a.nivel_efetivo === 'essencial';

/** Só o essencial (0030): componente/consumível some, a não ser que tenha essencial pendurado nele. */
function filtrarEssencial(itens: ItemArvore[]): ItemArvore[] {
  const porId = new Map(itens.map((a) => [a.id, a]));
  const manter = new Set<string>();
  for (const a of itens) {
    if (!essencial(a)) continue;
    for (let x: ItemArvore | undefined = a; x && !manter.has(x.id); x = x.ativo_pai_id ? porId.get(x.ativo_pai_id) : undefined) manter.add(x.id);
  }
  return itens.filter((a) => manter.has(a.id));
}

function montar(itens: ItemArvore[]): No[] {
  const porId = new Map(itens.map((a) => [a.id, { a, filhos: [] as No[], total: 0 }]));
  const raizes: No[] = [];
  for (const n of porId.values()) {
    const pai = n.a.ativo_pai_id ? porId.get(n.a.ativo_pai_id) : undefined;
    (pai ? pai.filhos : raizes).push(n);
  }
  const ordenar = (ns: No[]) => {
    ns.sort((x, y) => Number(!!y.a.agrupador) - Number(!!x.a.agrupador) || x.a.apelido.localeCompare(y.a.apelido, 'pt-BR', { numeric: true }));
    for (const n of ns) { ordenar(n.filhos); n.total = n.filhos.reduce((s, f) => s + 1 + f.total, 0); }
  };
  ordenar(raizes);
  return raizes;
}

export function ArvoreAtivos({ itens: todos, podeEditar, aoMudar }: { itens: ItemArvore[]; podeEditar: boolean; aoMudar: () => void }) {
  // abre mostrando só o essencial; componentes e consumíveis com um clique
  const [tudo, setTudo] = useState(() => { try { return localStorage.getItem('arvore.tudo') === '1'; } catch { return false; } });
  const itens = useMemo(() => (tudo ? todos : filtrarEssencial(todos)), [todos, tudo]);
  const ocultos = todos.length - filtrarEssencial(todos).length;
  const trocarTudo = () => setTudo((v) => { try { localStorage.setItem('arvore.tudo', v ? '0' : '1'); } catch { /* ok */ } return !v; });
  const parques = useMemo(() => {
    const m = new Map<string, { id: string; nome: string; itens: ItemArvore[] }>();
    for (const a of itens) {
      if (!m.has(a.local_id)) m.set(a.local_id, { id: a.local_id, nome: a.local, itens: [] });
      m.get(a.local_id)!.itens.push(a);
    }
    return [...m.values()].map((p) => ({ ...p, raizes: montar(p.itens) }));
  }, [itens]);
  // muitos itens: começa com as vias recolhidas (celular)
  const [abertos, setAbertos] = useState<Set<string>>(() => (itens.length > 40 ? new Set() : new Set(itens.map((a) => a.id))));
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [destino, setDestino] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [movendo, setMovendo] = useState(false);

  const parqueSel = useMemo(() => {
    const ls = new Set(itens.filter((a) => sel.has(a.id)).map((a) => a.local_id));
    return ls.size === 1 ? [...ls][0]! : null;
  }, [sel, itens]);
  const destinos = itens.filter((a) => a.local_id === parqueSel && !sel.has(a.id));

  const alternar = (id: string) => setAbertos((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const marcar = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function mover() {
    setErro(null); setMovendo(true);
    try {
      await api('/ativos/mover', { metodo: 'POST', corpo: { ids: [...sel], ativo_pai_id: destino === 'raiz' ? null : destino } });
      setSel(new Set()); setDestino(''); aoMudar();
    } catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível mover.'); }
    finally { setMovendo(false); }
  }

  const linha = (n: No, nivel: number): ReactNode => {
    const aberto = abertos.has(n.a.id);
    return (
      <li key={n.a.id}>
        <div className={`no-arvore${n.a.agrupador ? ' agrupador' : ''}${sel.has(n.a.id) ? ' marcado' : ''}`} style={{ ['--nivel' as string]: nivel }}>
          {podeEditar && <input type="checkbox" checked={sel.has(n.a.id)} onChange={() => marcar(n.a.id)} aria-label={`Selecionar ${n.a.apelido}`} />}
          {n.filhos.length > 0
            ? <button type="button" className="btn-icone abre" onClick={() => alternar(n.a.id)} aria-expanded={aberto} aria-label={aberto ? 'Recolher' : 'Abrir'}>
                {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
            : <span className="abre" aria-hidden />}
          <span className="nome">
            <Link to={`/ativos/${n.a.id}`}>{n.a.apelido}</Link>
            {(n.a.quantidade ?? 1) > 1 && <span className="tag-qtd">× {n.a.quantidade}</span>}
            {!essencial(n.a) && <span className="tag-nivel">{NOME_NIVEL[n.a.nivel_efetivo!]}</span>}
            {n.a.agrupador && <span className="tag-agrupador">{n.a.categoria.startsWith('Via') ? 'via' : 'setor'}{n.total ? ` · ${n.total}` : ''}</span>}
            {!n.a.agrupador && <small>{[n.a.categoria, [n.a.fabricante, n.a.modelo].filter(Boolean).join(' '), n.a.ip].filter(Boolean).join(' · ')}</small>}
          </span>
          {!n.a.agrupador && <span className={`status ativo-${n.a.status}`}>{NOME_STATUS_ATIVO[n.a.status]}</span>}
        </div>
        {aberto && n.filhos.length > 0 && <ul>{n.filhos.map((f) => linha(f, nivel + 1))}</ul>}
      </li>
    );
  };

  return (
    <>
      <div className="barra-arvore">
        <button className="btn-link" onClick={() => setAbertos(new Set(itens.map((a) => a.id)))}>abrir tudo</button>
        <button className="btn-link" onClick={() => setAbertos(new Set())}>recolher tudo</button>
        {(ocultos > 0 || tudo) && <button className="btn-link" onClick={trocarTudo}>{tudo ? 'só o essencial' : `mostrar componentes e consumíveis (${ocultos})`}</button>}
      </div>
      {parques.map((p) => (
        <section key={p.id} className="cartao parque-arvore">
          <header><MapPin size={16} aria-hidden /> <h2>{p.nome}</h2><span className="contador">{p.itens.filter((a) => !a.agrupador).length}</span></header>
          <ul className="arvore">{p.raizes.map((n) => linha(n, 1))}</ul>
        </section>
      ))}
      {podeEditar && sel.size > 0 && (
        <div className="barra-mover" role="region" aria-label="Mover selecionados">
          <strong>{sel.size} selecionado{sel.size > 1 ? 's' : ''}</strong>
          {parqueSel ? <>
            <select value={destino} onChange={(e) => setDestino(e.target.value)} aria-label="Mover para">
              <option value="">Mover para…</option>
              <option value="raiz">Direto no parque (solto)</option>
              <optgroup label="Vias e setores">{destinos.filter((a) => a.agrupador).map((a) => <option key={a.id} value={a.id}>{a.apelido}</option>)}</optgroup>
              <optgroup label="Dentro de um equipamento">{destinos.filter((a) => !a.agrupador).map((a) => <option key={a.id} value={a.id}>{a.apelido}</option>)}</optgroup>
            </select>
            <button className="btn" disabled={!destino || movendo} onClick={mover}>{movendo ? 'Movendo…' : 'Mover'}</button>
          </> : <span className="nota">Selecione itens de um parque só.</span>}
          <button className="btn-link" onClick={() => setSel(new Set())}>limpar</button>
          {erro && <p className="alerta" role="alert">{erro}</p>}
        </div>
      )}
    </>
  );
}
